/**
 * Accessibility fixes for portugalbestwines.co.uk
 * Addresses three issues flagging in the PageSpeed / Lighthouse audit.
 *
 * HOW TO DEPLOY:
 *   1. In Shopify Admin go to: Online Store > Themes > your live theme > Edit code
 *   2. In the Assets folder, click "Add a new asset" and upload this file,
 *      OR click on an existing "custom.js" file and paste the content in.
 *   3. Open layout/theme.liquid
 *   4. Just before the closing </body> tag, add this line:
 *        <script src="{{ 'accessibility-fixes.js' | asset_url }}" defer></script>
 *   5. Save. Done.
 */
 
(function () {
  'use strict';
 
  /* ============================================================
     FIX 1: aria-hidden slideshow slides with focusable children
     ------------------------------------------------------------
     Lighthouse flags any element that has aria-hidden="true" but
     still contains keyboard-focusable children (links, buttons).
     Screen readers hide the slide, but keyboard users can still
     tab into it — confusing and a WCAG 2.1 violation (1.3.1).
 
     Fix: whenever a slide becomes hidden, set tabindex="-1" on
     every interactive child. When it becomes visible again,
     restore normal focus by removing tabindex.
  ============================================================ */
 
  function syncSlideTabindex(slide) {
    const isHidden = slide.getAttribute('aria-hidden') === 'true';
    const focusable = slide.querySelectorAll(
      'a, button, input, select, textarea, [tabindex]'
    );
    focusable.forEach(function (el) {
      if (isHidden) {
        // Only set -1 if we haven't already stored the original value
        if (!el.hasAttribute('data-original-tabindex')) {
          el.setAttribute('data-original-tabindex', el.getAttribute('tabindex') || '');
        }
        el.setAttribute('tabindex', '-1');
      } else {
        // Restore the original tabindex (or remove it entirely)
        const original = el.getAttribute('data-original-tabindex');
        if (original === null || original === '') {
          el.removeAttribute('tabindex');
        } else {
          el.setAttribute('tabindex', original);
        }
        el.removeAttribute('data-original-tabindex');
      }
    });
  }
 
  function initSlidesFix() {
    // Run once on load for all slides currently in the DOM
    document.querySelectorAll('slideshow-slide').forEach(syncSlideTabindex);
 
    // Watch for aria-hidden changes as the carousel moves
    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'aria-hidden' &&
          mutation.target.tagName &&
          mutation.target.tagName.toLowerCase() === 'slideshow-slide'
        ) {
          syncSlideTabindex(mutation.target);
        }
      });
    });
 
    // Observe all slideshow containers
    document.querySelectorAll(
      'slideshow-component, resource-list, announcement-bar'
    ).forEach(function (container) {
      observer.observe(container, {
        attributes: true,
        attributeFilter: ['aria-hidden'],
        subtree: true,
      });
    });
  }
 
 
  /* ============================================================
     FIX 2: Empty links with no accessible name
     ------------------------------------------------------------
     Three <a class="group-block__link"> elements on the homepage
     point to /pages/reviews but contain no text, no aria-label,
     and no title. Screen readers announce them as "link" with no
     destination — unusable (WCAG 2.4.4).
 
     Fix: give each one a descriptive aria-label.
  ============================================================ */
 
  function initEmptyLinksFix() {
    document.querySelectorAll('a.group-block__link').forEach(function (link) {
      // Only patch links that are genuinely empty
      const hasText = link.textContent.trim().length > 0;
      const hasLabel =
        link.getAttribute('aria-label') ||
        link.getAttribute('aria-labelledby') ||
        link.getAttribute('title');
 
      if (!hasText && !hasLabel) {
        const href = link.getAttribute('href') || '';
 
        // Derive a sensible label from the href, falling back to a generic one
        if (href.includes('reviews')) {
          link.setAttribute('aria-label', 'Read our customer reviews');
        } else if (href.includes('about')) {
          link.setAttribute('aria-label', 'About us');
        } else if (href.includes('contact')) {
          link.setAttribute('aria-label', 'Contact us');
        } else {
          // Generic fallback — better than nothing
          link.setAttribute('aria-label', 'Learn more');
        }
      }
    });
  }
 
 
  /* ============================================================
     FIX 3: Multiple <nav> elements without aria-labels
     ------------------------------------------------------------
     When a page has more than one <nav> landmark, each must have
     a unique aria-label so screen reader users can tell them apart
     via the landmarks menu (WCAG 2.4.1).
 
     The four navs on this page:
       - menu-drawer__navigation  →  "Mobile menu"
       - main header nav          →  "Main menu"
       - account-actions__nav     →  "Account" (appears twice)
 
     Fix: assign aria-label to any <nav> that is missing one.
  ============================================================ */
 
  function initNavLabelsFix() {
    const accountNavCount = { count: 0 };
 
    document.querySelectorAll('nav').forEach(function (nav) {
      // Skip navs that already have a label
      if (nav.getAttribute('aria-label') || nav.getAttribute('aria-labelledby')) {
        return;
      }
 
      const classes = nav.className || '';
 
      if (classes.includes('menu-drawer')) {
        nav.setAttribute('aria-label', 'Mobile menu');
      } else if (classes.includes('account-actions')) {
        accountNavCount.count += 1;
        // There are two account navs (desktop/mobile); label them the same
        nav.setAttribute('aria-label', 'Account');
      } else {
        // The remaining unlabelled nav is the main desktop header nav
        nav.setAttribute('aria-label', 'Main menu');
      }
    });
  }
 
 
  /* ============================================================
     Initialise all fixes after the DOM is ready
  ============================================================ */
 
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initSlidesFix();
      initEmptyLinksFix();
      initNavLabelsFix();
    });
  } else {
    // DOM already ready (e.g. script loaded with defer)
    initSlidesFix();
    initEmptyLinksFix();
    initNavLabelsFix();
  }
 
})();